import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { 
  Filter, Upload, Play, Pause, Trash2, Download, 
  CheckCircle, XCircle, AlertCircle, Clock, Phone,
  Wifi, WifiOff, Plus, X
} from "lucide-react";
import { toast } from "sonner";

const API = '/api';

// Helper: Verificar si un perfil está realmente conectado (heartbeat reciente)
const isReallyConnected = (profile) => {
  if (profile.status !== 'connected') return false;
  if (!profile.last_seen) return false;
  
  const lastSeen = new Date(profile.last_seen);
  const now = new Date();
  const diffSeconds = (now - lastSeen) / 1000;
  
  // Considerar conectado solo si el último heartbeat fue hace menos de 30 segundos
  return diffSeconds < 30;
};

export default function RCSFilter() {
  const [jobs, setJobs] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const wsRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    profile_ids: [],  // Changed: now supports multiple profiles
    numbers_text: "",
    delay_min: 30,
    delay_max: 60,
    pause_after: 20,
    pause_duration: 300
  });

  const [quickCheckPhone, setQuickCheckPhone] = useState('');
  const [quickCheckLoading, setQuickCheckLoading] = useState(false);
  const [quickCheckResult, setQuickCheckResult] = useState(null);

  const connectedProfiles = profiles.filter(p => isReallyConnected(p));
  const fetchData = useCallback(async () => {
    try {
      const [jobsRes, profilesRes] = await Promise.all([
        axios.get(`${API}/filter-jobs`),
        axios.get(`${API}/profiles`)
      ]);
      setJobs(jobsRes.data);
      setProfiles(profilesRes.data);
    } catch (e) {
      console.error(e);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // WebSocket for real-time updates
  useEffect(() => {
    if (selectedJob) {
      const wsUrl = `${window.location.origin.replace('http', 'ws')}/ws/filter/${selectedJob}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === "filter_update") {
          setJobDetails(prev => prev ? { ...prev, job: data.job } : prev);
          fetchJobDetails(selectedJob);
        }
      };
      
      return () => { ws.close(); };
    }
     
  }, [selectedJob]);

  const fetchJobDetails = async (jobId) => {
    try {
      const res = await axios.get(`${API}/filter-jobs/${jobId}`);
      setJobDetails(res.data);
    } catch {
      // silently handle fetch error
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      // Parse CSV or plain text
      const lines = text.split(/[\r\n,;]+/).filter(line => line.trim());
      const numbers = lines.map(line => {
        // Extract phone numbers
        const match = line.match(/[\d+]+/);
        return match ? match[0] : "";
      }).filter(n => n.length > 5);
      
      setForm(prev => ({
        ...prev,
        numbers_text: numbers.join("\n")
      }));
      toast.success(`${numbers.length} numeros cargados`);
    };
    reader.readAsText(file);
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    
    const numbers = form.numbers_text
      .split(/[\r\n,;]+/)
      .map(n => n.trim())
      .filter(n => n.length > 5);
    
    if (numbers.length === 0) {
      toast.error("Ingresa al menos un numero");
      return;
    }

    if (form.profile_ids.length === 0) {
      toast.error("Selecciona al menos un perfil");
      return;
    }

    try {
      const res = await axios.post(`${API}/filter-jobs`, {
        name: form.name || `Filtrado ${new Date().toLocaleDateString()}`,
        profile_ids: form.profile_ids,
        numbers,
        delay_min: form.delay_min,
        delay_max: form.delay_max,
        pause_after: form.pause_after,
        pause_duration: form.pause_duration
      });
      
      toast.success(`Job creado con ${numbers.length} numeros para ${form.profile_ids.length} perfil(es)`);
      setShowCreate(false);
      setForm(prev => ({ ...prev, name: "", numbers_text: "", profile_ids: [] }));
      fetchData();
      setSelectedJob(res.data.id);
      fetchJobDetails(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear job");
    }
  };

  const handleStartJob = async (jobId) => {
    try {
      const res = await axios.post(`${API}/filter-jobs/${jobId}/start`);
      toast.success("Filtrado iniciado. La extension comenzara a procesar automaticamente.");
      toast.info("Si la extension no inicia, recarga la pagina de Google Messages.");
      fetchData();
    } catch (e) {
      toast.error("Error al iniciar");
    }
  };

  const handlePauseJob = async (jobId) => {
    try {
      await axios.post(`${API}/filter-jobs/${jobId}/pause`);
      toast.success("Filtrado pausado");
      fetchData();
    } catch (e) {
      toast.error("Error al pausar");
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm("Eliminar este trabajo de filtrado?")) return;
    try {
      await axios.delete(`${API}/filter-jobs/${jobId}`);
      toast.success("Eliminado");
      if (selectedJob === jobId) {
        setSelectedJob(null);
        setJobDetails(null);
      }
      fetchData();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const handleExport = (jobId, type) => {
    window.open(`${API}/filter-jobs/${jobId}/export?filter_type=${type}`, "_blank");
  };

  const handleQuickCheck = async () => {
    if (!quickCheckPhone.trim()) {
      toast.error("Ingresa un numero de telefono");
      return;
    }
    if (connectedProfiles.length === 0) {
      toast.error("No hay perfiles conectados. Conecta la extension primero.");
      return;
    }
    setQuickCheckLoading(true);
    setQuickCheckResult(null);
    try {
      const res = await axios.post(`${API}/filter-jobs/quick-check`, {
        phone: quickCheckPhone.trim(),
        profile_id: connectedProfiles[0].id
      });
      toast.success(res.data.message);
      setQuickCheckResult({ status: 'checking', jobId: res.data.job.id });
      // Poll for result
      const pollInterval = setInterval(async () => {
        try {
          const jobRes = await axios.get(`${API}/filter-jobs/${res.data.job.id}`);
          const entry = jobRes.data.entries?.[0];
          if (entry && entry.status !== 'pending' && entry.status !== 'checking') {
            clearInterval(pollInterval);
            setQuickCheckResult({ status: entry.status, phone: entry.phone });
            setQuickCheckLoading(false);
            fetchData();
          }
        } catch { /* ignore */ }
      }, 3000);
      // Timeout after 60 seconds
      setTimeout(() => {
        clearInterval(pollInterval);
        setQuickCheckLoading(false);
        if (!quickCheckResult || quickCheckResult.status === 'checking') {
          setQuickCheckResult({ status: 'timeout' });
        }
      }, 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al verificar");
      setQuickCheckLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-zinc-700 text-zinc-300",
      active: "bg-green-900/50 text-green-400",
      paused: "bg-yellow-900/50 text-yellow-400",
      completed: "bg-blue-900/50 text-blue-400"
    };
    const labels = {
      pending: "Pendiente",
      active: "Activo",
      paused: "Pausado",
      completed: "Completado"
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Filter className="w-6 h-6 text-cyan-500" />
            Filtrado RCS
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Verifica que numeros tienen RCS activo
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white px-4 py-2 rounded-lg transition-colors"
          data-testid="create-filter-btn"
        >
          <Plus className="w-4 h-4" />
          Nuevo Filtrado
        </button>
      </div>

      {/* Warning if no connected profiles */}
      {connectedProfiles.length === 0 && (
        <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-4 flex items-center gap-3">
          <WifiOff className="w-5 h-5 text-amber-500" />
          <div>
            <p className="text-amber-400 font-medium">Sin perfiles conectados</p>
            <p className="text-amber-400/70 text-sm">Conecta un perfil desde la extension para poder filtrar</p>
          </div>
        </div>
      )}

      {/* Instructions Banner */}
      <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
        <p className="text-blue-400 font-medium mb-2">Como funciona el filtrado:</p>
        <ol className="text-blue-400/70 text-sm space-y-1 list-decimal list-inside">
          <li>Crea un nuevo trabajo de filtrado con los numeros a verificar</li>
          <li>Haz clic en "Iniciar" en el trabajo creado</li>
          <li>La extension automaticamente comenzara a verificar cada numero</li>
          <li>Los resultados (RCS/SMS) apareceran en tiempo real</li>
        </ol>
        <p className="text-blue-400/70 text-sm mt-2">
          <strong>Importante:</strong> Asegurate de tener Google Messages abierto y la extension conectada.
        </p>
      </div>

      {/* Quick Check - Manual single number */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4" data-testid="quick-check-section">
        <h3 className="text-white font-medium mb-3 flex items-center gap-2">
          <Phone className="w-4 h-4 text-cyan-500" />
          Verificacion Rapida
        </h3>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <input
              type="text"
              value={quickCheckPhone}
              onChange={e => setQuickCheckPhone(e.target.value)}
              placeholder="+5493871234567"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-cyan-500"
              data-testid="quick-check-input"
              onKeyDown={e => e.key === 'Enter' && handleQuickCheck()}
            />
          </div>
          <button
            onClick={handleQuickCheck}
            disabled={quickCheckLoading || connectedProfiles.length === 0}
            className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2"
            data-testid="quick-check-btn"
          >
            {quickCheckLoading ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div> Verificando...</>
            ) : (
              <><Filter className="w-4 h-4" /> Verificar RCS</>
            )}
          </button>
        </div>
        {quickCheckResult && (
          <div className={`mt-3 p-2 rounded-lg text-sm flex items-center gap-2 ${
            quickCheckResult.status === 'rcs' ? 'bg-green-900/30 text-green-400' :
            quickCheckResult.status === 'sms' ? 'bg-yellow-900/30 text-yellow-400' :
            quickCheckResult.status === 'checking' ? 'bg-blue-900/30 text-blue-400' :
            quickCheckResult.status === 'timeout' ? 'bg-red-900/30 text-red-400' :
            'bg-zinc-800 text-zinc-400'
          }`} data-testid="quick-check-result">
            {quickCheckResult.status === 'rcs' && <><CheckCircle className="w-4 h-4" /> {quickCheckResult.phone}: RCS activo</>}
            {quickCheckResult.status === 'sms' && <><XCircle className="w-4 h-4" /> {quickCheckResult.phone}: Solo SMS</>}
            {quickCheckResult.status === 'checking' && <><Clock className="w-4 h-4 animate-spin" /> Verificando... (la extension esta procesando)</>}
            {quickCheckResult.status === 'timeout' && <><AlertCircle className="w-4 h-4" /> Tiempo agotado. Verifica que la extension esta conectada y Google Messages abierto.</>}
            {quickCheckResult.status === 'error' && <><AlertCircle className="w-4 h-4" /> Error al verificar</>}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">Nuevo Trabajo de Filtrado</h2>
              <button onClick={() => setShowCreate(false)} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateJob} className="p-6 space-y-4">
              <div>
                <label className="text-sm text-zinc-400 block mb-1">Nombre (opcional)</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                  placeholder="Mi filtrado..."
                />
              </div>

              <div>
                <label className="text-sm text-zinc-400 block mb-1">Perfiles (pueden ser varios)</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.profile_ids.map(pid => {
                    const profile = profiles.find(p => p.id === pid);
                    return (
                      <span key={pid} className="bg-cyan-900/30 text-cyan-400 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        {profile?.name || pid.slice(0,8)}
                        <button
                          type="button"
                          onClick={() => setForm(prev => ({
                            ...prev,
                            profile_ids: prev.profile_ids.filter(id => id !== pid)
                          }))}
                          className="hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value && !form.profile_ids.includes(e.target.value)) {
                      setForm(prev => ({
                        ...prev,
                        profile_ids: [...prev.profile_ids, e.target.value]
                      }));
                    }
                  }}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="">Agregar perfil...</option>
                  {profiles
                    .filter(p => !form.profile_ids.includes(p.id))
                    .map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} {isReallyConnected(p) ? '(conectado)' : '(desconectado)'}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-zinc-500 mt-1">
                  Selecciona uno o varios perfiles. El trabajo se distribuira entre ellos.
                </p>
              </div>

              <div>
                <label className="text-sm text-zinc-400 block mb-1">Numeros a verificar</label>
                <div className="flex gap-2 mb-2">
                  <label className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg px-3 py-2 cursor-pointer transition-colors">
                    <Upload className="w-4 h-4 text-cyan-500" />
                    <span className="text-sm text-white">Subir CSV</span>
                    <input type="file" accept=".csv,.txt" onChange={handleFileUpload} className="hidden" />
                  </label>
                </div>
                <textarea
                  value={form.numbers_text}
                  onChange={(e) => setForm({ ...form, numbers_text: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white h-40 font-mono text-sm"
                  placeholder="+5491123456789&#10;+5491198765432&#10;..."
                  required
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Un numero por linea. Se detectaran automaticamente.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Delay minimo (seg)</label>
                  <input
                    type="number"
                    value={form.delay_min}
                    onChange={(e) => setForm({ ...form, delay_min: parseInt(e.target.value) || 30 })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                    min="10"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Delay maximo (seg)</label>
                  <input
                    type="number"
                    value={form.delay_max}
                    onChange={(e) => setForm({ ...form, delay_max: parseInt(e.target.value) || 60 })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                    min="15"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Pausar cada X verificaciones</label>
                  <input
                    type="number"
                    value={form.pause_after}
                    onChange={(e) => setForm({ ...form, pause_after: parseInt(e.target.value) || 20 })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                    min="5"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Duracion pausa (seg)</label>
                  <input
                    type="number"
                    value={form.pause_duration}
                    onChange={(e) => setForm({ ...form, pause_duration: parseInt(e.target.value) || 300 })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                    min="60"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-2 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white py-2 rounded-lg transition-colors"
                  disabled={connectedProfiles.length === 0}
                >
                  Crear Filtrado
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Jobs List */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: Jobs */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Trabajos de Filtrado</h2>
          
          {jobs.length === 0 ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-8 text-center">
              <Filter className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No hay trabajos de filtrado</p>
              <p className="text-zinc-500 text-sm">Crea uno para empezar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map(job => (
                <div
                  key={job.id}
                  className={`bg-zinc-900/50 border rounded-xl p-4 cursor-pointer transition-all ${
                    selectedJob === job.id ? "border-cyan-500" : "border-zinc-800 hover:border-zinc-700"
                  }`}
                  onClick={() => {
                    setSelectedJob(job.id);
                    fetchJobDetails(job.id);
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-white">{job.name}</h3>
                    {getStatusBadge(job.status)}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-zinc-400 mb-3">
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {job.total_numbers} numeros
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {job.rcs_count} RCS
                    </span>
                    <span className="flex items-center gap-1">
                      <XCircle className="w-4 h-4 text-yellow-500" />
                      {job.sms_count} SMS
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden mb-3">
                    <div 
                      className="h-full bg-gradient-to-r from-cyan-500 to-green-500 transition-all"
                      style={{ width: `${(job.checked_count / job.total_numbers) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-zinc-500">{job.checked_count} / {job.total_numbers} verificados</p>

                  <div className="flex gap-2 mt-3">
                    {job.status === "pending" || job.status === "paused" ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStartJob(job.id); }}
                        className="flex items-center gap-1 bg-green-600 hover:bg-green-500 text-white px-3 py-1 rounded text-sm transition-colors"
                      >
                        <Play className="w-3 h-3" /> Iniciar
                      </button>
                    ) : job.status === "active" ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePauseJob(job.id); }}
                        className="flex items-center gap-1 bg-yellow-600 hover:bg-yellow-500 text-white px-3 py-1 rounded text-sm transition-colors"
                      >
                        <Pause className="w-3 h-3" /> Pausar
                      </button>
                    ) : null}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteJob(job.id); }}
                      className="flex items-center gap-1 bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1 rounded text-sm transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Details */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Resultados</h2>
          
          {!selectedJob || !jobDetails ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-8 text-center">
              <p className="text-zinc-400">Selecciona un trabajo para ver los resultados</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Export buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleExport(selectedJob, "all")}
                  className="flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  <Download className="w-4 h-4" /> Todos
                </button>
                <button
                  onClick={() => handleExport(selectedJob, "rcs")}
                  className="flex items-center gap-1 bg-green-600/20 hover:bg-green-600/40 text-green-400 px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  <Download className="w-4 h-4" /> Solo RCS
                </button>
                <button
                  onClick={() => handleExport(selectedJob, "sms")}
                  className="flex items-center gap-1 bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-400 px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  <Download className="w-4 h-4" /> Solo SMS
                </button>
              </div>

              {/* Results list */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
                <div className="max-h-96 overflow-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-800/50 sticky top-0">
                      <tr>
                        <th className="text-left text-xs text-zinc-400 font-medium px-4 py-2">Telefono</th>
                        <th className="text-center text-xs text-zinc-400 font-medium px-4 py-2">Tipo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobDetails.entries.map(entry => (
                        <tr key={entry.id} className="border-t border-zinc-800/50">
                          <td className="px-4 py-2 text-sm text-white font-mono">{entry.phone}</td>
                          <td className="px-4 py-2 text-center">
                            {entry.status === "rcs" ? (
                              <span className="inline-flex items-center gap-1 bg-green-900/30 text-green-400 px-2 py-0.5 rounded text-xs">
                                <Wifi className="w-3 h-3" /> RCS
                              </span>
                            ) : entry.status === "sms" ? (
                              <span className="inline-flex items-center gap-1 bg-yellow-900/30 text-yellow-400 px-2 py-0.5 rounded text-xs">
                                <WifiOff className="w-3 h-3" /> SMS
                              </span>
                            ) : entry.status === "error" ? (
                              <span className="inline-flex items-center gap-1 bg-red-900/30 text-red-400 px-2 py-0.5 rounded text-xs">
                                <AlertCircle className="w-3 h-3" /> Error
                              </span>
                            ) : entry.status === "checking" ? (
                              <span className="inline-flex items-center gap-1 bg-blue-900/30 text-blue-400 px-2 py-0.5 rounded text-xs">
                                <Clock className="w-3 h-3" /> Verificando
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 bg-zinc-700 text-zinc-400 px-2 py-0.5 rounded text-xs">
                                <Clock className="w-3 h-3" /> Pendiente
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
